/* eslint-disable react-refresh/only-export-components */
import { create } from 'zustand';

/**
 * Minimal toast system (the OG `_v8Toast`). `toast(text, kind)` can be called
 * from anywhere; <Toaster/> is mounted once in RootLayout.
 */
type ToastKind = 'ok' | 'info' | 'error';
interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
}
interface ToastState {
  toasts: ToastItem[];
  push: (text: string, kind?: ToastKind) => void;
  remove: (id: number) => void;
}

let seq = 0;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (text, kind = 'info') => {
    const id = ++seq;
    set((s) => ({ toasts: [...s.toasts, { id, text, kind }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 2600);
  },
  remove: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Fire a toast from anywhere (components, handlers, mutations). */
export const toast = (text: string, kind: ToastKind = 'info') =>
  useToastStore.getState().push(text, kind);

const KIND_RING: Record<ToastKind, string> = {
  ok: 'before:bg-[#34C759]',
  info: 'before:bg-white/60',
  error: 'before:bg-[#ff6b6b]',
};

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-24 left-1/2 z-[300] flex -translate-x-1/2 flex-col items-center gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`animate-fade-in-up glass-elevated relative flex items-center gap-2 rounded-pill py-2.5 pl-4 pr-5 text-[13px] font-semibold text-hi shadow-[0_12px_32px_rgba(0,0,0,0.5)] before:h-1.5 before:w-1.5 before:rounded-full ${KIND_RING[t.kind]}`}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}
